import { useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'next-i18next';
import { Button } from '@dx-ui/global-ui-prism';
import { JoinModal, JoinModalProps, type JoinModalAnalytics } from './osc-join-modal';

type JoinModalButtonProps = {
  /** Wrapper button classname */
  className?: string;
  /** Button label */
  children?: ReactNode;
  /** Headline for the join form */
  headline?: JoinModalProps['headline'];
  language: JoinModalProps['language'];
  /** Enrollment OCode, defaults to OHWGW */
  oCode?: JoinModalProps['oCode'];
  /** Metrics object from join form */
  metrics: JoinModalProps['metrics'];
  onClick?: () => void;
  onDismiss?: () => void;
  analytics?: JoinModalAnalytics;
  onSuccessfulJoin?: JoinModalProps['onSuccessfulJoin'];
  onSuccessfulLogin?: JoinModalProps['onSuccessfulLogin'];
};

/** This is a generic button component that will launch a modal with the join form when clicked. */
export const JoinModalButton: React.FC<JoinModalButtonProps> = ({
  children,
  className,
  onClick,
  onDismiss,
  ...rest
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation('osc-join-modal-button');

  const handleOpenModal = () => {
    setIsModalOpen(true);
    onClick?.();
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    onDismiss?.();
  };

  return (
    <JoinModal
      {...rest}
      dialogTrigger={
        <Button
          className={className}
          label={children ?? t('join')}
          ref={buttonRef}
          onClick={handleOpenModal}
        />
      }
      isOpen={isModalOpen}
      onDismiss={handleCloseModal}
    />
  );
};
